import { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  OverlayDrawer,
  Select,
  Spinner
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { MessageBarType } from '@fluentui/react';
import { useTranslation } from 'react-i18next';

import { AppState } from '../../../redux/reducers';
import { AvailableMedicalService } from '../../../models/Entities/MedicalServices/AvailableMedicalService';
import MedicalServiceSelectorComponent from '../../../commons/component/Inputs/MedicalServiceSelector/MedicalServiceSelectorComponent';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';

import * as ComponentFunctions from './PriceListItemForm.Functions';
import { useStyles } from './PriceListItemForm.Styles';

const PriceListItemFormComponentBase = (props: ComponentFunctions.Props) => {
  const styles = useStyles();
  const [t] = useTranslation();

  const PriceState = useSelector((appState: AppState) => appState.PriceDetails);

  const [submittedForm, setSubmittedForm] = useState<boolean>(false);
  const [formValidation, setFormValidation] = useState<
    ComponentFunctions.FormValidation | undefined
  >();
  const [formState, setFormState] = useState<ComponentFunctions.PriceFormState>(
    ComponentFunctions.initialPriceFormState
  );

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (props.PriceDetails) {
      setFormState(ComponentFunctions.formStateFromPriceDetails(props.PriceDetails));
    } else {
      ResetForm();
    }
  }, [props.PriceDetails]);

  useEffect(() => {
    if (
      submittedForm &&
      PriceState &&
      (PriceState.successAddingNewOne || PriceState.successUpdatingOne)
    ) {
      ResetForm();
      props.dismissPanel();
    }
  }, [PriceState?.successAddingNewOne, PriceState?.successUpdatingOne, submittedForm]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const ResetForm = () => {
    setFormState(ComponentFunctions.initialPriceFormState);
    setFormValidation(undefined);
    setSubmittedForm(false);
  };

  const patch = (partial: Partial<ComponentFunctions.PriceFormState>) =>
    setFormState((s) => ({ ...s, ...partial }));

  // ── Events ─────────────────────────────────────────────────────────────────

  const HandleCancelForm = () => {
    ResetForm();
    props.dismissPanel();
  };

  const HandleSaveForm = () => {
    setSubmittedForm(true);
    const validation = ComponentFunctions.validateForm(formState, t);
    setFormValidation(validation);

    if (validation.success) {
      if (props.PriceDetails) {
        props.SavePrice(
          ComponentFunctions.buildPricePayload(formState, props.PriceDetails, props.PriceListId)
        );
      } else {
        props.AddNewPrice(
          ComponentFunctions.buildNewPricePayload(formState, props.PriceListId)
        );
      }
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const isSaving =
    (PriceState?.isAddingNewOne || PriceState?.isUpdatingOne) ?? false;

  const shouldShowFriendlySaveError =
    submittedForm &&
    PriceState &&
    (PriceState.failOnAddingNewOne || PriceState.failOnUpdatingOne) &&
    PriceState.error &&
    (PriceState.error.ErrorCode === 400 || PriceState.error.ErrorCode === 409);

  return (
    <OverlayDrawer position="end" open={props.isOpen}>
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label={t('priceListItemForm.modal.closeButton')}
              icon={<Dismiss24Regular />}
              onClick={HandleCancelForm}
            />
          }
        >
          {props.PriceDetails
            ? t('priceListItemForm.modal.editTitle')
            : t('priceListItemForm.modal.priceInformation')}
        </DrawerHeaderTitle>

        {submittedForm && formValidation?.success === false && (
          <MessageComponent
            message={t('priceListItemForm.modal.reviewInformation')}
            type={MessageBarType.error}
            itemList={formValidation.errors}
          />
        )}

        {shouldShowFriendlySaveError ? (
          <MessageComponent
            message={t('medicalSpecialties.priceListItemForm.failMessage.duplicatePrice')}
            type={MessageBarType.error}
          />
        ) : (
          submittedForm &&
          PriceState &&
          (PriceState.failOnAddingNewOne || PriceState.failOnUpdatingOne) &&
          PriceState.error && <ErrorMessageComponent Error={PriceState.error} />
        )}
      </DrawerHeader>

      <DrawerBody className={styles.drawerBody}>
        <MedicalServiceSelectorComponent
          onSelectedServices={(services: AvailableMedicalService[] | undefined) => {
            if (services && services.length > 0) {
              patch({
                itemReferredId: services[0].id,
                itemReferredName: services[0].serviceNameReference
              });
            }
          }}
          MedicalServicesSelectedIds={
            formState.itemReferredId ? [formState.itemReferredId] : undefined
          }
          MultiselectEnable={false}
        />

        <Field
          label={t('priceListItemForm.parameters.currency')}
          validationMessage={
            submittedForm && !formState.currency
              ? t('priceListItemForm.parameters.requiredField')
              : undefined
          }
        >
          <Select
            aria-label={t('priceListItemForm.parameters.currency')}
            value={formState.currency ?? ''}
            onChange={(_, data) =>
              patch({ currency: data.value || undefined })
            }
          >
            <option value="">{t('priceListItemForm.parameters.selectCurrency')}</option>
            {ComponentFunctions.currencyOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.text}
              </option>
            ))}
          </Select>
        </Field>

        {props.showPrice && (
          <Field
            label={t('priceListItemForm.parameters.price')}
            required
            validationMessage={
              submittedForm && formState.price === undefined
                ? t('priceListItemForm.parameters.requiredField')
                : undefined
            }
          >
            <Input
              type="number"
              value={formState.price?.toString() ?? ''}
              onChange={(_, data) =>
                patch({ price: data.value === '' ? undefined : Number(data.value) })
              }
            />
          </Field>
        )}

        {props.showRemotePrice && (
          <Field label={t('priceListItemForm.parameters.remotePrice')}>
            <Input
              type="number"
              value={formState.remotePrice.toString()}
              onChange={(_, data) => patch({ remotePrice: Number(data.value) || 0 })}
            />
          </Field>
        )}

        {props.showPreScreeningPrice && (
          <Field label={t('priceListItemForm.parameters.preScreeningPrice')}>
            <Input
              type="number"
              value={formState.preScreeningPrice.toString()}
              onChange={(_, data) =>
                patch({ preScreeningPrice: Number(data.value) || 0 })
              }
            />
          </Field>
        )}

        {props.showFailPrice && (
          <Field label={t('priceListItemForm.parameters.failPrice')}>
            <Input
              type="number"
              value={formState.failPrice.toString()}
              onChange={(_, data) => patch({ failPrice: Number(data.value) || 0 })}
            />
          </Field>
        )}

        {props.showRandomPrice && (
          <Field label={t('priceListItemForm.parameters.randomPrice')}>
            <Input
              type="number"
              value={formState.randomPrice.toString()}
              onChange={(_, data) => patch({ randomPrice: Number(data.value) || 0 })}
            />
          </Field>
        )}

        {props.showAdditionalPaymentPrice && (
          <Field label={t('priceListItemForm.parameters.additionalPaymentPrice')}>
            <Input
              type="number"
              value={formState.additionalPaymentPrice.toString()}
              onChange={(_, data) =>
                patch({ additionalPaymentPrice: Number(data.value) || 0 })
              }
            />
          </Field>
        )}
      </DrawerBody>

      <DrawerFooter className={styles.footer}>
        <Button onClick={HandleCancelForm} disabled={isSaving}>
          {t('priceListItemForm.footer.cancelButton')}
        </Button>
        <Button appearance="primary" onClick={HandleSaveForm} disabled={isSaving}>
          {isSaving && (
            <>
              <Spinner size="extra-tiny" />
              &nbsp;
            </>
          )}
          {t('priceListItemForm.footer.saveButton')}
        </Button>
      </DrawerFooter>
    </OverlayDrawer>
  );
};

export const PriceListItemFormComponent = connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(PriceListItemFormComponentBase as any) as any;

export default PriceListItemFormComponent;
